import { useState, useEffect, createContext, useContext } from "react";

const ADMIN_API = "https://amathematics.in/backend/admin";
const AdminAuthCtx = createContext(null);
const useAdminAuth = () => useContext(AdminAuthCtx);

// ===== COLORS: White + Orange (NeilPatel style) + Dark blue accents =====
const C = {
  bg: "#f8f9fb",
  sidebar: "#0d1b3e",
  sidebarActive: "#ff6b35",
  white: "#ffffff",
  orange: "#ff6b35",
  orangeLight: "#fff3ee",
  orangeDark: "#e05520",
  text: "#1a2744",
  textMuted: "#6a80a0",
  border: "#e8ecf4",
  cardBg: "#ffffff",
  blue: "#0d1b3e",
  blueLight: "#f0f4ff",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
};

const s = (styles) => ({ ...styles });

function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_user")); } catch(e) { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [checking, setChecking] = useState(false);

  const login = (t, a) => {
    setToken(t); setAdmin(a);
    localStorage.setItem("admin_token", t);
    localStorage.setItem("admin_user", JSON.stringify(a));
  };
  const logout = () => {
    setToken(null); setAdmin(null);
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
  };
  return <AdminAuthCtx.Provider value={{ admin, token, login, logout, checking }}>{children}</AdminAuthCtx.Provider>;
}

function adminFetch(path, token, opts = {}) {
  return fetch(`${ADMIN_API}/${path}`, {
    ...opts, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) }
  }).then(r => r.json());
}

// ===== STAT CARD =====
function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "24px", display: "flex", alignItems: "center", gap: "16px", boxShadow: "0 2px 8px rgba(0,0,0,.04)" }}>
      <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: color || C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: "26px", fontWeight: 800, color: C.text }}>{value}</div>
        <div style={{ fontSize: "13px", color: C.textMuted }}>{label}</div>
        {sub && <div style={{ fontSize: "12px", color: C.success, marginTop: "2px" }}>{sub}</div>}
      </div>
    </div>
  );
}

// ===== DASHBOARD =====
function Dashboard({ token }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminFetch("dashboard.php?action=stats", token).then(d => setStats(d)); }, [token]);
  if (!stats) return <div style={{ padding: "40px", color: C.textMuted, textAlign: "center" }}>Loading dashboard...</div>;

  return (
    <div>
      <h2 style={{ color: C.text, marginBottom: "24px", fontSize: "22px", fontWeight: 700 }}>Dashboard Overview</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "20px", marginBottom: "32px" }}>
        <StatCard icon="👥" label="Total Users" value={stats.stats.total_users.toLocaleString()} sub={`+${stats.stats.today_users} today`} />
        <StatCard icon="🧮" label="Total Queries" value={stats.stats.total_queries.toLocaleString()} sub={`+${stats.stats.today_queries} today`} color="#f0f4ff" />
        <StatCard icon="💰" label="Total Revenue" value={`$${parseFloat(stats.stats.total_revenue).toFixed(2)}`} color="#f0fff4" />
        <StatCard icon="📊" label="Avg Queries/User" value={stats.stats.total_users > 0 ? (stats.stats.total_queries / stats.stats.total_users).toFixed(1) : 0} color="#fff8f0" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "32px" }}>
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "24px" }}>
          <h3 style={{ color: C.text, margin: "0 0 16px", fontSize: "16px" }}>Plan Distribution</h3>
          {stats.plan_distribution.map(p => (
            <div key={p.plan} style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
              <div style={{ width: "80px", fontSize: "13px", color: C.textMuted, textTransform: "capitalize" }}>{p.plan}</div>
              <div style={{ flex: 1, background: C.border, borderRadius: "4px", height: "8px" }}>
                <div style={{ width: `${Math.min(100, (p.cnt / stats.stats.total_users) * 100)}%`, height: "100%", background: p.plan === "pro" ? C.orange : p.plan === "basic" ? "#3b82f6" : p.plan === "enterprise" ? "#8b5cf6" : "#e8ecf4", borderRadius: "4px" }} />
              </div>
              <div style={{ width: "30px", textAlign: "right", fontSize: "13px", fontWeight: 600, color: C.text }}>{p.cnt}</div>
            </div>
          ))}
        </div>

        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "24px" }}>
          <h3 style={{ color: C.text, margin: "0 0 16px", fontSize: "16px" }}>Recent Payments</h3>
          {stats.recent_payments.slice(0, 5).map(p => (
            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: C.text }}>{p.name}</div>
                <div style={{ fontSize: "12px", color: C.textMuted }}>{p.gateway} · {new Date(p.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ color: C.text, fontWeight: 700 }}>${p.amount}</span>
                <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "11px", background: p.status === "completed" ? "#f0fff4" : "#fff8f0", color: p.status === "completed" ? C.success : C.warning }}>{p.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "24px" }}>
        <h3 style={{ color: C.text, margin: "0 0 16px", fontSize: "16px" }}>Recent Users</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Name", "Email", "Plan", "Joined"].map(h => <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontSize: "12px", color: C.textMuted, fontWeight: 600, background: C.bg, borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {stats.recent_users.map(u => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: "12px", fontSize: "14px", fontWeight: 600, color: C.text }}>{u.name}</td>
                <td style={{ padding: "12px", fontSize: "13px", color: C.textMuted }}>{u.email}</td>
                <td style={{ padding: "12px" }}>
                  <span style={{ padding: "3px 10px", borderRadius: "10px", fontSize: "12px", background: u.plan === "pro" ? "#fff3ee" : u.plan === "basic" ? "#f0f4ff" : C.bg, color: u.plan === "pro" ? C.orange : u.plan === "basic" ? "#3b82f6" : C.textMuted, textTransform: "capitalize" }}>{u.plan}</span>
                </td>
                <td style={{ padding: "12px", fontSize: "13px", color: C.textMuted }}>{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== USERS MANAGEMENT =====
function UsersPanel({ token }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const load = () => adminFetch(`dashboard.php?action=users&page=${page}&search=${search}`, token).then(d => { setUsers(d.users || []); setTotal(d.total || 0); });
  useEffect(() => { load(); }, [page, search]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2 style={{ color: C.text, margin: 0, fontSize: "22px", fontWeight: 700 }}>Users ({total})</h2>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search users..."
          style={{ padding: "10px 16px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "14px", outline: "none", width: "240px" }} />
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.bg }}>{["#", "Name", "Email", "Plan", "Queries", "Joined", "Actions"].map(h => <th key={h} style={{ textAlign: "left", padding: "12px 16px", fontSize: "12px", color: C.textMuted, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}` }} onMouseEnter={e => e.currentTarget.style.background = C.bg} onMouseLeave={e => e.currentTarget.style.background = ""}>
                <td style={{ padding: "14px 16px", fontSize: "13px", color: C.textMuted }}>{u.id}</td>
                <td style={{ padding: "14px 16px", fontWeight: 600, color: C.text, fontSize: "14px" }}>{u.name}</td>
                <td style={{ padding: "14px 16px", fontSize: "13px", color: C.textMuted }}>{u.email}</td>
                <td style={{ padding: "14px 16px" }}>
                  <span style={{ padding: "3px 10px", borderRadius: "10px", fontSize: "12px", background: u.plan === "pro" ? "#fff3ee" : u.plan === "basic" ? "#f0f4ff" : C.bg, color: u.plan === "pro" ? C.orange : "#3b82f6", textTransform: "capitalize" }}>{u.plan}</span>
                </td>
                <td style={{ padding: "14px 16px", fontSize: "13px", color: C.text }}>{u.queries_used}/{u.queries_limit === -1 ? "∞" : u.queries_limit}</td>
                <td style={{ padding: "14px 16px", fontSize: "13px", color: C.textMuted }}>{new Date(u.created_at).toLocaleDateString()}</td>
                <td style={{ padding: "14px 16px" }}>
                  <button onClick={() => { if (window.confirm("Delete user?")) adminFetch(`dashboard.php?action=delete_user&id=${u.id}`, token).then(() => load()); }}
                    style={{ padding: "4px 12px", background: "#fff0f0", border: `1px solid #fcc`, borderRadius: "6px", color: C.danger, cursor: "pointer", fontSize: "12px" }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: "13px", color: C.textMuted }}>Showing {users.length} of {total} users</span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: "6px 14px", border: `1px solid ${C.border}`, borderRadius: "8px", cursor: "pointer", background: page === 1 ? C.bg : C.white, color: C.text }}>← Prev</button>
            <button onClick={() => setPage(p => p + 1)} disabled={users.length < 20}
              style={{ padding: "6px 14px", border: `1px solid ${C.border}`, borderRadius: "8px", cursor: "pointer", background: C.white, color: C.text }}>Next →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== SETTINGS FORM =====
function SettingsSection({ title, fields, values, onChange }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "24px", marginBottom: "24px" }}>
      <h3 style={{ color: C.text, margin: "0 0 20px", fontSize: "16px", fontWeight: 700, display: "flex", alignItems: "center", gap: "8px" }}>{title}</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        {fields.map(f => (
          <div key={f.key} style={f.full ? { gridColumn: "1/-1" } : {}}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.text, marginBottom: "6px" }}>{f.label}</label>
            {f.type === "textarea" ? (
              <textarea value={values[f.key] || ""} onChange={e => onChange(f.key, e.target.value)} rows={4}
                style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", outline: "none", resize: "vertical", boxSizing: "border-box", fontFamily: "monospace" }} />
            ) : f.type === "select" ? (
              <select value={values[f.key] || ""} onChange={e => onChange(f.key, e.target.value)}
                style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", outline: "none", background: C.white }}>
                {f.options?.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ) : (
              <input type={f.type || "text"} value={values[f.key] || ""} onChange={e => onChange(f.key, e.target.value)}
                placeholder={f.placeholder || ""}
                style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", outline: "none", boxSizing: "border-box" }} />
            )}
            {f.hint && <p style={{ fontSize: "12px", color: C.textMuted, margin: "4px 0 0" }}>{f.hint}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel({ token }) {
  const [activeTab, setActiveTab] = useState("general");
  const [settings, setSettings] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminFetch("settings.php?action=get", token).then(d => setSettings(d.settings || {}));
  }, [token]);

  const updateSetting = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const saveSettings = async (group) => {
    await adminFetch("settings.php?action=save", token, { method: "POST", body: JSON.stringify({ group, settings }) });
    setSaved(true); setTimeout(() => setSaved(false), 2500);
  };

  const tabs = [
    { id: "general", label: "⚙️ General" },
    { id: "ai", label: "🤖 AI / OpenAI" },
    { id: "payment", label: "💳 Payments" },
    { id: "seo", label: "🔍 SEO" },
    { id: "email", label: "📧 Email" },
    { id: "plans", label: "📦 Plans" },
    { id: "security", label: "🔒 Security" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "24px" }}>
        <h2 style={{ color: C.text, margin: 0, fontSize: "22px", fontWeight: 700 }}>Settings</h2>
        {saved && <div style={{ padding: "8px 20px", background: "#f0fff4", border: `1px solid #a7f3d0`, borderRadius: "10px", color: C.success, fontSize: "14px", fontWeight: 600 }}>✓ Settings saved!</div>}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "24px" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: "8px 16px", borderRadius: "10px", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: 600, background: activeTab === t.id ? C.orange : C.white, color: activeTab === t.id ? "#fff" : C.textMuted, border: `1px solid ${activeTab === t.id ? C.orange : C.border}`, transition: "all .2s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "general" && <>
        <SettingsSection title="🌐 Site Information" fields={[
          { key: "site_name", label: "Site Name", placeholder: "MathAI Tutor" },
          { key: "site_tagline", label: "Tagline", placeholder: "Your AI Math Tutor" },
          { key: "site_url", label: "Site URL", placeholder: "https://yourdomain.com" },
          { key: "site_email", label: "Contact Email" },
          { key: "maintenance_mode", label: "Maintenance Mode", type: "select", options: [{ v: "0", l: "Off" }, { v: "1", l: "On" }] },
          { key: "registration_enabled", label: "Registration", type: "select", options: [{ v: "1", l: "Enabled" }, { v: "0", l: "Disabled" }] },
        ]} values={settings} onChange={updateSetting} />
        <button onClick={() => saveSettings("general")} style={{ padding: "12px 32px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}>Save General Settings</button>
      </>}

      {activeTab === "ai" && <>
        <SettingsSection title="🤖 OpenAI Configuration" fields={[
          { key: "openai_api_key", label: "OpenAI API Key", type: "password", placeholder: "sk-...", full: true, hint: "Get your API key from platform.openai.com" },
          { key: "openai_model", label: "Model", type: "select", options: [{ v: "gpt-4", l: "GPT-4 (Recommended)" }, { v: "gpt-4-turbo", l: "GPT-4 Turbo" }, { v: "gpt-3.5-turbo", l: "GPT-3.5 Turbo (Cheaper)" }] },
          { key: "openai_max_tokens", label: "Max Tokens", type: "number", placeholder: "2000" },
          { key: "free_queries_limit", label: "Free Queries Limit", type: "number", placeholder: "10" },
          { key: "system_prompt", label: "System Prompt", type: "textarea", full: true, hint: "Customize how the AI tutor responds" },
        ]} values={settings} onChange={updateSetting} />
        <button onClick={() => saveSettings("ai")} style={{ padding: "12px 32px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}>Save AI Settings</button>
      </>}

      {activeTab === "payment" && <>
        <SettingsSection title="💳 Stripe" fields={[
          { key: "stripe_publishable_key", label: "Publishable Key", placeholder: "pk_live_..." },
          { key: "stripe_secret_key", label: "Secret Key", type: "password", placeholder: "sk_live_..." },
          { key: "stripe_webhook_secret", label: "Webhook Secret", type: "password", placeholder: "whsec_..." },
        ]} values={settings} onChange={updateSetting} />
        <SettingsSection title="🅿 PayPal" fields={[
          { key: "paypal_client_id", label: "Client ID" },
          { key: "paypal_client_secret", label: "Client Secret", type: "password" },
          { key: "paypal_mode", label: "Mode", type: "select", options: [{ v: "sandbox", l: "Sandbox (Testing)" }, { v: "live", l: "Live" }] },
        ]} values={settings} onChange={updateSetting} />
        <SettingsSection title="₹ Razorpay" fields={[
          { key: "razorpay_key_id", label: "Key ID" },
          { key: "razorpay_key_secret", label: "Key Secret", type: "password" },
        ]} values={settings} onChange={updateSetting} />
        <button onClick={() => saveSettings("payment")} style={{ padding: "12px 32px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}>Save Payment Settings</button>
      </>}

      {activeTab === "seo" && <>
        <SettingsSection title="🔍 SEO & Analytics" fields={[
          { key: "google_analytics_id", label: "Google Analytics ID", placeholder: "G-XXXXXXXXXX" },
          { key: "google_search_console_verification", label: "GSC Verification Code", placeholder: "google-site-verification=..." },
          { key: "robots_txt", label: "robots.txt Content", type: "textarea", full: true },
        ]} values={settings} onChange={updateSetting} />
        <div style={{ background: "#fff8f0", border: `1px solid #fed7aa`, borderRadius: "12px", padding: "16px", marginBottom: "16px" }}>
          <p style={{ margin: 0, fontSize: "13px", color: "#92400e" }}>
            📌 <strong>Sitemap:</strong> Auto-generated at <code>https://yourdomain.com/backend/api/seo.php?action=sitemap</code><br />
            📌 <strong>Robots.txt:</strong> Served at <code>https://yourdomain.com/robots.txt</code> (via .htaccess redirect)
          </p>
        </div>
        <button onClick={() => saveSettings("seo")} style={{ padding: "12px 32px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}>Save SEO Settings</button>
      </>}

      {activeTab === "email" && <>
        <SettingsSection title="📧 SMTP Email Configuration" fields={[
          { key: "smtp_host", label: "SMTP Host", placeholder: "smtp.gmail.com" },
          { key: "smtp_port", label: "SMTP Port", placeholder: "587" },
          { key: "smtp_user", label: "SMTP Username" },
          { key: "smtp_pass", label: "SMTP Password", type: "password" },
          { key: "smtp_from_name", label: "From Name", placeholder: "MathAI Tutor" },
        ]} values={settings} onChange={updateSetting} />
        <button onClick={() => saveSettings("email")} style={{ padding: "12px 32px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "15px" }}>Save Email Settings</button>
      </>}

      {activeTab === "security" && <ChangePasswordPanel token={token} />}
    </div>
  );
}

function ChangePasswordPanel({ token }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const submit = async () => {
    if (form.new_password !== form.confirm) { setErr("Passwords don't match"); return; }
    const d = await adminFetch("auth.php?action=change_password", token, { method: "POST", body: JSON.stringify(form) });
    if (d.error) setErr(d.error); else { setMsg("Password changed successfully!"); setForm({ current_password: "", new_password: "", confirm: "" }); }
  };

  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "32px", maxWidth: "480px" }}>
      <h3 style={{ color: C.text, margin: "0 0 24px", fontSize: "18px" }}>🔒 Change Admin Password</h3>
      {err && <div style={{ background: "#fff0f0", border: `1px solid #fcc`, borderRadius: "8px", padding: "10px 14px", color: C.danger, fontSize: "13px", marginBottom: "16px" }}>{err}</div>}
      {msg && <div style={{ background: "#f0fff4", border: `1px solid #a7f3d0`, borderRadius: "8px", padding: "10px 14px", color: C.success, fontSize: "13px", marginBottom: "16px" }}>✓ {msg}</div>}
      {[["current_password", "Current Password"], ["new_password", "New Password"], ["confirm", "Confirm New Password"]].map(([k, l]) => (
        <div key={k} style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.text, marginBottom: "6px" }}>{l}</label>
          <input type="password" value={form[k]} onChange={e => setForm(p => ({ ...p, [k]: e.target.value }))}
            style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "14px", outline: "none", boxSizing: "border-box" }} />
        </div>
      ))}
      <button onClick={submit} style={{ padding: "12px 28px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Update Password</button>
    </div>
  );
}

// ===== SEO PAGES =====
function SEOPanel({ token }) {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState("home");
  const [form, setForm] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminFetch(`settings.php?action=get_seo&slug=${selected}`, token).then(d => {
      setForm(d.page || { page_slug: selected });
      setPages(d.all_pages || []);
    });
  }, [selected, token]);

  const save = async () => {
    await adminFetch("settings.php?action=save_seo", token, { method: "POST", body: JSON.stringify({ ...form, page_slug: selected }) });
    setSaved(true); setTimeout(() => setSaved(false), 2500);
  };

  const fields = [
    { k: "title", l: "Page Title" },
    { k: "meta_description", l: "Meta Description", type: "textarea" },
    { k: "meta_keywords", l: "Meta Keywords" },
    { k: "og_title", l: "OG Title" },
    { k: "og_description", l: "OG Description", type: "textarea" },
    { k: "og_image", l: "OG Image URL" },
    { k: "robots", l: "Robots", type: "select", opts: ["index,follow", "noindex,nofollow", "index,nofollow", "noindex,follow"] },
    { k: "schema_markup", l: "Schema Markup (JSON-LD)", type: "textarea" },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2 style={{ color: C.text, margin: 0, fontSize: "22px", fontWeight: 700 }}>SEO Management</h2>
        {saved && <div style={{ padding: "8px 20px", background: "#f0fff4", border: `1px solid #a7f3d0`, borderRadius: "10px", color: C.success, fontSize: "14px" }}>✓ Saved!</div>}
      </div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
        {["home", "pricing", "about", "contact", ...pages.map(p => p.page_slug).filter(s => !["home", "pricing", "about", "contact"].includes(s))].map(slug => (
          <button key={slug} onClick={() => setSelected(slug)}
            style={{ padding: "7px 16px", borderRadius: "8px", border: `1px solid ${selected === slug ? C.orange : C.border}`, background: selected === slug ? C.orangeLight : C.white, color: selected === slug ? C.orange : C.textMuted, cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
            {slug}
          </button>
        ))}
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "28px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {fields.map(f => (
            <div key={f.k} style={["meta_description", "og_description", "schema_markup"].includes(f.k) ? { gridColumn: "1/-1" } : {}}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.text, marginBottom: "6px" }}>{f.l}</label>
              {f.type === "textarea" ? (
                <textarea value={form[f.k] || ""} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))} rows={3}
                  style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
              ) : f.type === "select" ? (
                <select value={form[f.k] || "index,follow"} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", outline: "none" }}>
                  {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input value={form[f.k] || ""} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", outline: "none", boxSizing: "border-box" }} />
              )}
            </div>
          ))}
        </div>
        <div style={{ marginTop: "24px", display: "flex", gap: "12px", alignItems: "center" }}>
          <button onClick={save} style={{ padding: "12px 28px", background: C.orange, border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Save SEO</button>
          <span style={{ fontSize: "13px", color: C.textMuted }}>Editing: /{selected}</span>
        </div>
      </div>
    </div>
  );
}

// ===== ADMIN LOGIN =====
function AdminLogin() {
  const { login } = useAdminAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.email || !form.password) { setError("Please enter email and password"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${ADMIN_API}/auth.php?action=login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const d = await res.json();
      setLoading(false);
      if (d.error) { setError(d.error); return; }
      if (!d.token) { setError("Login failed — no token received. Check backend."); return; }
      login(d.token, d.admin);
    } catch(err) {
      setLoading(false);
      setError("Cannot reach server. Check: 1) Backend files are uploaded 2) CORS header is set to https://amathematics.in");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(135deg, ${C.blue} 0%, #1a2744 100%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{ background: C.white, borderRadius: "24px", padding: "48px", maxWidth: "420px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ width: "60px", height: "60px", background: `linear-gradient(135deg,${C.orange},${C.orangeDark})`, borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", margin: "0 auto 16px" }}>∑</div>
          <h1 style={{ color: C.text, margin: "0 0 6px", fontSize: "24px", fontWeight: 800 }}>MathAI Admin</h1>
          <p style={{ color: C.textMuted, margin: 0, fontSize: "14px" }}>Sign in to your admin panel</p>
        </div>
        {error && <div style={{ background: "#fff0f0", border: `1px solid #fcc`, borderRadius: "10px", padding: "12px 16px", color: C.danger, fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.text, marginBottom: "6px" }}>Email Address</label>
          <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="admin@yourdomain.com"
            style={{ width: "100%", padding: "12px 16px", border: `1px solid ${C.border}`, borderRadius: "12px", fontSize: "14px", outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: "24px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: C.text, marginBottom: "6px" }}>Password</label>
          <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="••••••••"
            onKeyDown={e => e.key === "Enter" && submit()}
            style={{ width: "100%", padding: "12px 16px", border: `1px solid ${C.border}`, borderRadius: "12px", fontSize: "14px", outline: "none", boxSizing: "border-box" }} />
        </div>
        <button onClick={submit} disabled={loading}
          style={{ width: "100%", padding: "14px", background: `linear-gradient(135deg,${C.orange},${C.orangeDark})`, border: "none", borderRadius: "12px", color: "#fff", fontWeight: 700, fontSize: "16px", cursor: "pointer" }}>
          {loading ? "Signing in..." : "Sign In to Admin"}
        </button>
        <p style={{ textAlign: "center", color: C.textMuted, fontSize: "12px", marginTop: "20px" }}>Default: admin@mathtutor.com / Admin@123</p>
      </div>
    </div>
  );
}

// ===== ADMIN LAYOUT =====
const NAV_ITEMS = [
  { id: "dashboard", icon: "📊", label: "Dashboard" },
  { id: "users", icon: "👥", label: "Users" },
  { id: "queries", icon: "🧮", label: "Queries" },
  { id: "settings", icon: "⚙️", label: "Settings" },
  { id: "seo", icon: "🔍", label: "SEO" },
  { id: "plans", icon: "📦", label: "Plans" },
];

function AdminLayout() {
  const { admin, token, logout } = useAdminAuth();
  const [page, setPage] = useState("dashboard");

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.bg, fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      {/* SIDEBAR */}
      <aside style={{ width: "240px", background: C.sidebar, flexShrink: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "24px 20px", borderBottom: "1px solid rgba(255,255,255,.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "36px", height: "36px", background: `linear-gradient(135deg,${C.orange},${C.orangeDark})`, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>∑</div>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>MathAI</div>
              <div style={{ color: C.orange, fontSize: "10px", letterSpacing: "1px" }}>ADMIN PANEL</div>
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: "16px 12px" }}>
          {NAV_ITEMS.map(item => (
            <button key={item.id} onClick={() => setPage(item.id)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: "10px", padding: "11px 14px", background: page === item.id ? `rgba(255,107,53,.15)` : "transparent", border: page === item.id ? `1px solid rgba(255,107,53,.3)` : "1px solid transparent", borderRadius: "10px", marginBottom: "4px", cursor: "pointer", color: page === item.id ? C.orange : "rgba(255,255,255,.6)", fontWeight: page === item.id ? 600 : 400, fontSize: "14px", transition: "all .15s", textAlign: "left" }}>
              <span style={{ fontSize: "16px" }}>{item.icon}</span> {item.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,.1)" }}>
          <div style={{ color: "rgba(255,255,255,.7)", fontSize: "13px", marginBottom: "10px" }}>
            <div style={{ fontWeight: 600, color: "#fff" }}>{admin?.name}</div>
            <div style={{ color: C.orange, fontSize: "11px" }}>{admin?.role?.toUpperCase()}</div>
          </div>
          <button onClick={logout} style={{ width: "100%", padding: "8px", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: "8px", color: "rgba(255,255,255,.6)", cursor: "pointer", fontSize: "13px" }}>Sign Out</button>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex: 1, overflowY: "auto" }}>
        <header style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: C.text }}>{NAV_ITEMS.find(n => n.id === page)?.label || "Dashboard"}</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <a href="/" target="_blank" style={{ padding: "7px 16px", background: C.orangeLight, border: `1px solid ${C.orange}`, borderRadius: "8px", color: C.orange, textDecoration: "none", fontSize: "13px", fontWeight: 600 }}>↗ View Site</a>
          </div>
        </header>
        <div style={{ padding: "32px" }}>
          {page === "dashboard" && <Dashboard token={token} />}
          {page === "users" && <UsersPanel token={token} />}
          {page === "settings" && <SettingsPanel token={token} />}
          {page === "seo" && <SEOPanel token={token} />}
          {(page === "queries" || page === "plans") && (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "16px", padding: "40px", textAlign: "center", color: C.textMuted }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>{page === "queries" ? "🧮" : "📦"}</div>
              <h3 style={{ color: C.text }}>{page === "queries" ? "Query History" : "Plan Management"}</h3>
              <p>This panel loads data from your backend. Connect your API to view and manage {page}.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// ===== ADMIN APP ROOT =====
export default function AdminApp() {
  return (
    <AdminAuthProvider>
      <AdminAppInner />
    </AdminAuthProvider>
  );
}

function AdminAppInner() {
  const { admin } = useAdminAuth();
  if (admin) return <AdminLayout />;
  return <AdminLogin />;
}
